/**
 * @module PlatformEventLog
 * @purpose Read-only list of recent platform-level audit events (demo-mode toggles,
 *   provider switches) for the platform admin settings page.
 * @depends hooks/use-platform-events, i18n/hooks/use-formatters
 * @dependedOnBy pages/PlatformSettingsPage
 *
 * @rules
 * - Performance: single fetch via usePlatformEvents; no own polling
 * - State: none beyond the hook -- read-only, no delete affordance
 * - Errors: renders a translated failure message; no raw error surfaced
 */

import { useTranslation } from 'react-i18next'
import { usePlatformEvents } from '../../hooks/use-platform-events'
import { useFormatters } from '../../i18n/hooks/use-formatters'
import type { PlatformEvent } from '../../types/platform'

const CARD_STYLE: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  padding: '24px',
}

const TITLE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '1.1rem',
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: '12px',
}

const ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '8px 0',
  borderBottom: '1px solid var(--border)',
  fontFamily: 'var(--font-body)',
  fontSize: '0.825rem',
  color: 'var(--text-dim)',
}

const META_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.7rem',
  color: 'var(--text-muted, var(--text-dim))',
  whiteSpace: 'nowrap',
}

function EventRow({ event, formatDate }: { event: PlatformEvent; formatDate: (value: string) => string }): React.ReactElement {
  return (
    <li style={ROW_STYLE}>
      <span>{event.message}</span>
      <span style={META_STYLE}>
        {event.actor} · {formatDate(event.occurredAt)}
      </span>
    </li>
  )
}

/**
 * Platform event log card.
 *
 * @behaviour Renders the loading, error, empty, and success states for the platform
 *            event feed. Events are rendered in the order the backend returns them
 *            (newest-first, server-side) -- never re-sorted client-side.
 * @errors Shows a translated failure message; never the raw error string.
 */
export function PlatformEventLog(): React.ReactElement {
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')
  const { events, loading, error } = usePlatformEvents()
  const { formatDate } = useFormatters()

  return (
    <div style={CARD_STYLE}>
      <h3 style={TITLE_STYLE}>{t('demoMode.events.title')}</h3>
      {loading && <p style={{ color: 'var(--text-dim)' }}>{tCommon('actions.loading')}</p>}
      {!loading && error !== null && (
        <p role="alert" style={{ color: 'rgb(220, 38, 38)' }}>
          {t('demoMode.events.loadFailed')}
        </p>
      )}
      {!loading && error === null && events.length === 0 && (
        <p style={{ color: 'var(--text-dim)' }}>{t('demoMode.events.empty')}</p>
      )}
      {!loading && error === null && events.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {events.map((event) => (
            <EventRow key={event.id} event={event} formatDate={formatDate} />
          ))}
        </ul>
      )}
    </div>
  )
}
